/**
 * Чистая логика уведомлений (без Vue/axios) — удобно тестировать.
 */

export const HR_PLATFORM_NOTIFICATION_SOURCE = 'hr_platform'

export function normalizeNotificationSource(source) {
  const normalized = String(source || '').trim().toLowerCase()
  if (['hr', 'hh', 'hh.ru', 'hr-platform', HR_PLATFORM_NOTIFICATION_SOURCE].includes(normalized)) {
    return HR_PLATFORM_NOTIFICATION_SOURCE
  }
  return normalized
}

/** Старые HR-сообщения до поля source определяем по entity_type. */
export function isNotificationVisibleInERP(message) {
  if (!message || typeof message !== 'object') return false
  if (normalizeNotificationSource(message.source) === HR_PLATFORM_NOTIFICATION_SOURCE) return false
  if (!message.source) {
    const entityType = String(message.entity_type || '').trim().toLowerCase()
    if (entityType === 'hr_event' || entityType === 'hiring_request') return false
  }
  return true
}

export function filterERPNotifications(messages) {
  if (!Array.isArray(messages)) return []
  return messages.filter(isNotificationVisibleInERP)
}

export function withERPNotificationSourceFilter(params = {}) {
  return {
    ...(params && typeof params === 'object' ? params : {}),
    exclude_source: HR_PLATFORM_NOTIFICATION_SOURCE,
  }
}

export function formatRelativeTime(isoOrDate, now = Date.now()) {
  if (!isoOrDate) return ''
  const ts = typeof isoOrDate === 'number' ? isoOrDate : new Date(isoOrDate).getTime()
  if (!Number.isFinite(ts)) return ''
  const diff = Math.max(0, now - ts)
  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return 'только что'
  if (minutes < 60) return `${minutes} мин назад`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} ч назад`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days} дн назад`
  return new Date(ts).toLocaleDateString('ru-RU')
}

export function roleSectionTitle(roleName) {
  const name = (roleName || '').trim()
  if (!name) return 'Уведомления для роли'
  return `Уведомления: ${name}`
}

export function formatUnreadBadge(count) {
  const n = Number(count) || 0
  if (n <= 0) return ''
  if (n > 99) return '99+'
  return String(n)
}

/** @returns {{ user_id: string } | null} */
export function buildPersonalMessagesParams(userId) {
  if (!userId) return null
  return { user_id: String(userId) }
}

/** @returns {{ role_id: number } | null} */
export function buildRoleMessagesParams(roleId) {
  const id = Number(roleId)
  if (!Number.isFinite(id) || id <= 0) return null
  return { role_id: id }
}

/** @returns {{ channel_id: number } | null} */
export function buildChannelMessagesParams(channelId) {
  const id = Number(channelId)
  if (!Number.isFinite(id) || id <= 0) return null
  return { channel_id: id }
}

export function hasMessageFilterParams(params) {
  return !!params && typeof params === 'object' && Object.keys(params).length > 0
}

export function findSettingsForChannel(settingsList, channelId) {
  const id = Number(channelId)
  if (!Array.isArray(settingsList) || !Number.isFinite(id)) return null
  return settingsList.find((s) => Number(s.channel_id) === id) || null
}

export function parseUnreadCountPayload(data) {
  if (data == null) return 0
  if (typeof data === 'number') return data
  if (typeof data.count === 'number') return data.count
  return Number(data.count) || 0
}

export function parseMarkReadPayload(data) {
  if (data == null) return 0
  if (typeof data === 'number') return data
  if (typeof data.marked === 'number') return data.marked
  return Number(data.marked) || 0
}

/** @returns {number|null} */
export function getMessageId(msg) {
  const id = Number(msg?.id)
  if (!Number.isFinite(id) || id <= 0) return null
  return id
}

export function isMessageUnread(msg) {
  return !!msg && !msg.is_read
}

/** Прочитанным помечаем только по клику на конкретное уведомление. */
export function shouldMarkMessageAsRead(msg) {
  return getMessageId(msg) != null && isMessageUnread(msg)
}

export function markMessageReadInList(list, messageId) {
  const id = Number(messageId)
  if (!Array.isArray(list)) return []
  if (!Number.isFinite(id) || id <= 0) return list
  let changed = false
  const next = list.map((m) => {
    if (Number(m?.id) !== id || m?.is_read) return m
    changed = true
    return { ...m, is_read: true }
  })
  return changed ? next : list
}

export function decrementUnreadCount(count) {
  return Math.max(0, (Number(count) || 0) - 1)
}

export function channelUnreadCount(channel) {
  return Math.max(0, Number(channel?.unread_count) || 0)
}

export function channelUnreadBadge(channel) {
  return formatUnreadBadge(channelUnreadCount(channel))
}

export function decrementChannelUnreadInList(list, channelId) {
  const id = Number(channelId)
  if (!Array.isArray(list)) return []
  if (!Number.isFinite(id) || id <= 0) return list
  let changed = false
  const next = list.map((ch) => {
    if (Number(ch?.id) !== id) return ch
    const n = channelUnreadCount(ch)
    if (n <= 0) return ch
    changed = true
    return { ...ch, unread_count: n - 1 }
  })
  return changed ? next : list
}

export const NOTIFICATION_VIEWS = Object.freeze({
  MENU: 'menu',
  PERSONAL: 'personal',
  ROLE: 'role',
  CHANNELS: 'channels',
  CHANNEL: 'channel',
})

export const NOTIFICATION_INBOX_SECTIONS = Object.freeze({
  PERSONAL: 'personal',
  ROLE: 'role',
  CHANNELS: 'channels',
})

/** Раздел полной страницы уведомлений из query (?section=). */
export function parseInboxSection(raw) {
  const value = String(raw || '').trim().toLowerCase()
  if (value === NOTIFICATION_INBOX_SECTIONS.ROLE) return NOTIFICATION_INBOX_SECTIONS.ROLE
  if (value === NOTIFICATION_INBOX_SECTIONS.CHANNELS) return NOTIFICATION_INBOX_SECTIONS.CHANNELS
  return NOTIFICATION_INBOX_SECTIONS.PERSONAL
}

export function parseInboxChannelId(raw) {
  const id = Number(raw)
  if (!Number.isFinite(id) || id <= 0) return null
  return id
}
